import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, ChevronDown, LogOut, User, BookOpen, LayoutDashboard } from 'lucide-react';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/services', label: 'Services' },
    { to: '/educenter', label: 'Altera Scholar' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? 'rgba(6,13,31,0.97)' : 'rgba(6,13,31,0.85)',
      borderBottom: `1px solid ${scrolled ? 'rgba(0,200,255,0.15)' : 'transparent'}`,
      backdropFilter: 'blur(12px)',
      transition: 'all 0.3s ease',
    }}>
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '68px' }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <img 
            src="/logo.png" 
            alt="AlteraCode" 
            style={{ 
              height: 42, 
              width: 'auto',
              objectFit: 'contain',
            }} 
          />
        </Link>

        {/* Desktop nav */}
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }} className="desktop-nav">
          {navLinks.map(l => (
            <Link key={l.to} to={l.to} style={{
              color: location.pathname === l.to ? 'var(--ac-cyan)' : 'var(--ac-muted)',
              textDecoration: 'none', fontSize: 14, fontWeight: 500,
              transition: 'color 0.2s',
              borderBottom: location.pathname === l.to ? '1px solid var(--ac-cyan)' : 'none',
              paddingBottom: 2,
            }}
              onMouseEnter={e => e.target.style.color = 'var(--ac-white)'}
              onMouseLeave={e => e.target.style.color = location.pathname === l.to ? 'var(--ac-cyan)' : 'var(--ac-muted)'}
            >{l.label}</Link>
          ))}
        </div>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {user ? (
            <div style={{ position: 'relative' }} ref={userMenuRef}>
              <button onClick={() => setUserMenuOpen(!userMenuOpen)} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--ac-card)', border: '1px solid var(--ac-border)',
                borderRadius: 'var(--radius-md)', padding: '8px 14px',
                color: 'var(--ac-white)', cursor: 'pointer', fontSize: 14,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'var(--grad-edu)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: 'white',
                }}>{user.name?.charAt(0).toUpperCase()}</div>
                {user.name?.split(' ')[0]}
                <ChevronDown size={14} />
              </button>
              {userMenuOpen && (
                <div style={{
                  position: 'absolute', top: '110%', right: 0,
                  background: 'var(--ac-card2)', border: '1px solid var(--ac-border)',
                  borderRadius: 'var(--radius-md)', minWidth: 200,
                  boxShadow: 'var(--shadow-card)', overflow: 'hidden', zIndex: 100,
                }}>
                  {user.role === 'admin' && (
                    <Link to="/admin" style={ddStyle} onClick={() => setUserMenuOpen(false)}>
                      <LayoutDashboard size={15} /> Admin Panel
                    </Link>
                  )}
                  <Link to="/dashboard" style={ddStyle} onClick={() => setUserMenuOpen(false)}>
                    <BookOpen size={15} /> My Courses
                  </Link>
                  <Link to="/profile" style={ddStyle} onClick={() => setUserMenuOpen(false)}>
                    <User size={15} /> Profile
                  </Link>
                  <div style={{ height: 1, background: 'var(--ac-border)', margin: '4px 0' }} />
                  <button onClick={handleLogout} style={{ ...ddStyle, width: '100%', color: 'var(--ac-danger)', border: 'none', cursor: 'pointer', background: 'transparent' }}>
                    <LogOut size={15} /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="navbar-auth" style={{ display: 'flex', gap: 8 }}>
              <Link to="/login" className="btn btn-ghost btn-sm">Log in</Link>
              <Link to="/register" className="btn btn-primary btn-sm">Get Started</Link>
            </div>
          )}
          {/* Mobile hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)} style={{
            display: 'none', background: 'none', border: 'none', color: 'var(--ac-white)', cursor: 'pointer',
          }} className="mobile-menu-btn">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{
          background: 'var(--ac-navy)', borderTop: '1px solid var(--ac-border)',
          padding: '1rem 1.5rem',
        }}>
          {navLinks.map(l => (
            <Link key={l.to} to={l.to} style={{
              display: 'block', padding: '10px 0',
              color: location.pathname === l.to ? 'var(--ac-cyan)' : 'var(--ac-text)',
              textDecoration: 'none', fontSize: 15, borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}>{l.label}</Link>
          ))}
          {!user ? (
            <div style={{ display: 'flex', gap: 10, marginTop: '1rem' }}>
              <Link to="/login" className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center' }}>Log in</Link>
              <Link to="/register" className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: 'center' }}>Get Started</Link>
            </div>
          ) : (
            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--ac-border)' }}>
              <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', color: 'var(--ac-text)', textDecoration: 'none', fontSize: 15 }}>
                <BookOpen size={16} /> My Courses
              </Link>
              <Link to="/profile" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', color: 'var(--ac-text)', textDecoration: 'none', fontSize: 15, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <User size={16} /> Profile
              </Link>
              <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', color: 'var(--ac-danger)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, width: '100%', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <LogOut size={16} /> Logout
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}

const ddStyle = {
  display: 'flex', alignItems: 'center', gap: 9,
  padding: '10px 16px', color: 'var(--ac-text)',
  fontSize: 14, textDecoration: 'none',
  transition: 'background 0.15s',
  cursor: 'pointer',
};
